import { useEffect, useMemo, useState } from "react"
import styled from "styled-components"

import { useLazyGetAlertsQuery } from "store/api/alertsApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconEye } from "assets/svg/icon-eye.svg"

import { convertAlertsToTableRows } from "utils/convertData"
import { isMobileDevice } from "utils/deviceDetection"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import { useAlertViewDetails } from "hooks/useAlertViewDetails"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AlertsActions } from "components/common/AlertsActions"
import { ContentWrapper } from "components/common/ContentWrapper"
import { DateRangeTabsFilter } from "components/common/DateRangeTabsFilter"
import ExportButton from "components/common/ExportButton"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import TableWrapper from "components/table/TableWrapper"

import { alertsTableHeaderCells } from "./AlertsTableHeaderCells"

import styles from "./index.module.scss"

const StyledTableNavWrapper = styled.nav`
  min-height: 90px;
  align-items: flex-start;
  position: relative;
`

const RowWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0px;
`

const DateFilterContainer = styled.div`
  padding-right: 10px;
  margin-right: auto;
`

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`

const AlertsActionsContainer = styled.div`
  position: absolute;
  bottom: 5px;
`

interface AlertsPageContentProps {
  alertsList: ApiTypes.Model.Alert[]
  isFetching: boolean
  meta: ApiTypes.Model.Pagination | undefined
  onRefreshAlerts: () => void
  urlQueryParams: Record<string, string>
}

export const AlertsPageContent = ({
  alertsList,
  isFetching,
  meta,
  onRefreshAlerts,
  urlQueryParams,
}: AlertsPageContentProps) => {
  const [selectedAlerts, setSelectedAlerts] = useState<ApiTypes.Model.Alert[]>([])
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()

  const isHeaderChecked = useMemo(() => {
    if (!alertsList.length) return false
    return alertsList.every(alert => selectedAlerts.some(s => s.id === alert.id))
  }, [alertsList, selectedAlerts])
  const { setUrlQueryParams } = useUrlQueryParams()
  const {
    selectedDevice,
    monitoringReportAnnotations,
    handleViewDetails,
    clearMonitoringReportAnnotations,
    closeStatsModal,
  } = useAlertViewDetails()

  useEffect(() => {
    triggerGetTableColumns({ list_name: "contractor_alerts_list" })
  }, [])

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(alertsTableHeaderCells, columnsData?.data?.config)
        : alertsTableHeaderCells,
    [columnsData],
  )

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta,
    fileName: "contractor_alerts",
    convertData: convertAlertsToTableRows,
    useLazyQuery: useLazyGetAlertsQuery,
  })

  const onSelectAlert = (id: number) => {
    if (selectedAlerts.some(item => item.id === id)) {
      setSelectedAlerts(selectedAlerts.filter(item => item.id !== id))
    } else {
      setSelectedAlerts([...selectedAlerts, alertsList.find(item => item.id === id)!])
    }
  }

  const handleSuccess = () => {
    onRefreshAlerts()
    setSelectedAlerts([])
  }

  const toggleAllAlerts = () => {
    if (isHeaderChecked) {
      const currentPageAlertIds = alertsList.map(a => a.id)
      setSelectedAlerts(prev => prev.filter(a => !currentPageAlertIds.includes(a.id)))
    } else {
      setSelectedAlerts(prev => {
        const existingIds = new Set(prev.map(a => a.id))
        const toAdd = alertsList.filter(a => !existingIds.has(a.id))
        return [...prev, ...toAdd]
      })
    }
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    if (rowData.type?.value !== "Efficiency Issue") {
      return {
        className: "",
        actionButtons: [],
      }
    }

    return {
      className: styles.messageTemplateButtonContainer,
      actionButtons: [
        {
          icon: <IconEye />,
          tooltip: "View on chart",
          tooltipPosition: "top-start" as const,
          hidden: rowData.rowSettings.isEnable === false,
          handler: () => handleViewDetails(rowData),
        },
      ],
    }
  }

  return (
    <ContentWrapper title="Alerts History">
      <StyledTableNavWrapper className={styles.tableNavWrapper}>
        <RowWrapper>
          <DateFilterContainer>
            <DateRangeTabsFilter paramName="date" urlQueryParams={urlQueryParams} onParamsChange={setUrlQueryParams} />
          </DateFilterContainer>
          <ButtonsContainer>
            <EditColumnsButton tableName="contractor_alerts_list" tableHeaderCells={tableHeaderCells} />
            <ExportButton onClick={handleExport} disabled={meta?.total === 0} />
          </ButtonsContainer>
        </RowWrapper>
        <AlertsActionsContainer>
          <AlertsActions
            selectedAlerts={selectedAlerts}
            onSuccess={handleSuccess}
            selectedDevice={selectedDevice}
            monitoringReportAnnotations={monitoringReportAnnotations}
            onClearMonitoringReportAnnotations={clearMonitoringReportAnnotations}
            onCloseStatsModal={closeStatsModal}
          />
        </AlertsActionsContainer>
      </StyledTableNavWrapper>
      <TableWrapper
        headerCells={tableHeaderCells}
        columnsLoaded={!!columnsData}
        data={convertAlertsToTableRows(alertsList)}
        isLoading={isFetching}
        onCheckboxChange={onSelectAlert}
        currentItemIDs={selectedAlerts.map(item => item.id)}
        getRowActions={getRowActions}
        onHeaderCheckboxChange={toggleAllAlerts}
        isHeaderChecked={isHeaderChecked}
        isMobileDevice={isMobileDevice()}
        skippedTags={["date"]}
        additionalTags={
          selectedAlerts?.length
            ? [
                {
                  label: "Selected Alerts",
                  values: [selectedAlerts.length],
                },
              ]
            : []
        }
        clearAdditionalTags={() => setSelectedAlerts([])}
      />
      <Pagination meta={meta} />
    </ContentWrapper>
  )
}
